import { useState, useCallback } from 'react';
import { BACKEND_URL } from '../config/appConfig';
import { toast } from 'sonner';
import * as mealsService from '../services/mealsService';

/**
 * useMealData — Manages today's meals, totals, nutrition goals, achievements,
 * dashboard stage, and quick-start state. Extracted from App.js Phase 5 Heavy Batch Round 1.
 */
export default function useMealData({ userId, userTimezone, accountAgeDays, isPremium }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [todayMeals, setTodayMeals] = useState([]);
  const [todayTotals, setTodayTotals] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0 });
  const [mealsLoaded, setMealsLoaded] = useState(false);
  const [totalMealsLogged, setTotalMealsLogged] = useState(0);
  const [nutritionGoals, setNutritionGoals] = useState(null);
  const [firstMealLogged, setFirstMealLogged] = useState(false);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [dashboardStage, setDashboardStage] = useState(1);
  const [achievements, setAchievements] = useState(null);
  const [shownAchievements, setShownAchievements] = useState({
    protein: false,
    carbs: false,
    fat: false,
    fifty_percent: false
  });

  // ── Calculate dashboard stage based on logged days ─────────────────────────
  const calculateDashboardStage = useCallback((mealsCount, daysSinceSignup) => {
    if (daysSinceSignup >= 5 || mealsCount >= 5) {
      setDashboardStage(2);
      return 2;
    }
    setDashboardStage(1);
    return 1;
  }, []);

  // ── Load today's meals ─────────────────────────────────────────────────────
  const loadTodayMeals = useCallback(async (uid) => {
    let totals = { calories: 0 };
    try {
      const data = await mealsService.getTodayMeals(uid, userTimezone);
      const meals = data.meals || [];
      totals = data.totals || { calories: 0 };
      setTodayMeals(meals);
      setTodayTotals(totals);

      // Track total meals for Quick Start logic
      setTotalMealsLogged(meals.length);

      // Track if first meal has been logged
      setFirstMealLogged(prev => {
        if (meals.length > 0 && !prev) return true;
        return prev;
      });

      // Calculate dashboard stage
      calculateDashboardStage(meals.length, accountAgeDays);

      // Set QuickStart based on meal count
      if (meals.length === 0) {
        setShowQuickStart(true);
      } else {
        setShowQuickStart(false);
      }

      if (data.goals) {
        setNutritionGoals(data.goals);
      }

      if (data.achievements) {
        setAchievements(prevAchievements => {
          const oldAchievements = prevAchievements || {};

          // Show trophy notifications for new achievements
          if (isPremium) {
            if (data.achievements.protein_goal_reached && !oldAchievements.protein_goal_reached) {
              setTimeout(() => alert('🏆 Protein Goal Achieved! Great job on hitting your protein target!'), 500);
              setShownAchievements(prev => ({ ...prev, protein: true }));
            }
            if (data.achievements.carbs_goal_reached && !oldAchievements.carbs_goal_reached) {
              setTimeout(() => alert('🏆 Carbs Goal Achieved! You\'ve reached your carbohydrate target!'), 500);
              setShownAchievements(prev => ({ ...prev, carbs: true }));
            }
            if (data.achievements.fat_goal_reached && !oldAchievements.fat_goal_reached) {
              setTimeout(() => alert('🏆 Fat Goal Achieved! Perfect balance on your fat intake!'), 500);
              setShownAchievements(prev => ({ ...prev, fat: true }));
            }
            if (data.achievements.fifty_percent_milestone && data.achievements.calorie_progress_percent >= 50 && data.achievements.calorie_progress_percent < 100) {
              setShownAchievements(prev => {
                if (!prev.fifty_percent) {
                  setTimeout(() => alert('💪 Halfway There! You\'ve reached 50% of your daily calorie goal. Keep it up!'), 500);
                  return { ...prev, fifty_percent: true };
                }
                return prev;
              });
            }
          }

          return data.achievements;
        });
      }

      return totals;
    } catch (error) {
      console.error('Error loading meals:', error);
      return null;
    } finally {
      setMealsLoaded(true);
    }
  }, [userTimezone, accountAgeDays, isPremium, calculateDashboardStage]);

  // ── Delete a meal ──────────────────────────────────────────────────────────
  const deleteMeal = useCallback(async (mealId) => {
    console.log('[deleteMeal] Attempting to delete meal:', mealId);

    if (!mealId) {
      console.error('[deleteMeal] No meal ID provided');
      alert('Error: Could not find meal ID to delete');
      return;
    }

    if (!window.confirm('Delete this meal?')) return;

    try {
      await mealsService.deleteMeal(mealId);
      await loadTodayMeals(userId);
      toast.success('Meal deleted successfully');
    } catch (error) {
      console.error('[deleteMeal] Error deleting meal:', error);
      toast.error('Failed to delete meal. Please try again.');
    }
  }, [userId, loadTodayMeals]);

  return {
    todayMeals, setTodayMeals,
    todayTotals, setTodayTotals,
    mealsLoaded, setMealsLoaded,
    totalMealsLogged, setTotalMealsLogged,
    nutritionGoals, setNutritionGoals,
    firstMealLogged, setFirstMealLogged,
    showQuickStart, setShowQuickStart,
    dashboardStage,
    achievements,
    shownAchievements, setShownAchievements,
    calculateDashboardStage,
    loadTodayMeals,
    deleteMeal
  };
}
